import { useState } from 'react'
import {
    Box,
    CircularProgress,
    Collapse,
    Divider,
    IconButton,
    Table,
    Tooltip,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography, Chip,
} from '@mui/material'
import { Assignment, DeleteOutline, ExpandLess, ExpandMore } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { useDeleteReport, useGetReportsByMainComponentId } from 'src/api/useInteractiveSchema'
import { toast } from 'react-toastify'
import { InteractiveSchemaReport, ReportStatus } from 'src/api/interactive-schema-types'
import { InteractiveSchemaReportViewer } from 'src/components/interactive-schema/InteractiveSchemaReportViewer'

interface Props {
    mainComponentId: number
}

export const InteractiveSchemaReportsInDocuments = ({ mainComponentId }: Props) => {
    const { t } = useTranslation(['interactive-schema', 'common'])
    const [expanded, setExpanded] = useState(true)
    const { showDialog, showConfirmDialog } = useDialogCtx()
    const deleteReportMutation = useDeleteReport()
    const { data: allReports = [], isLoading } = useGetReportsByMainComponentId(mainComponentId)
    const reports = allReports.filter(r => r.status === ReportStatus.Completed || r.status === ReportStatus.Approved)

    if (isLoading) return <CircularProgress size={20} sx={{ m: 2 }} />
    if (reports.length === 0) return null

    const handleDelete = (e: React.MouseEvent, report: InteractiveSchemaReport) => {
        e.stopPropagation()
        showConfirmDialog(
            t('delete-report', { ns: 'interactive-schema' }),
            t('delete-report-confirm', { ns: 'interactive-schema', name: report.reportName }),
            { acceptText: t('delete', { ns: 'common' }), cancelText: t('cancel', { ns: 'common' }) },
            async pop => {
                await deleteReportMutation.mutateAsync(report.id, {
                    onSuccess: () => {
                        toast.success(t('report-deleted', { ns: 'interactive-schema' }))
                        pop()
                    },
                })
            },
        )
    }

    const handleRowClick = (report: InteractiveSchemaReport) => {
        showDialog(
            InteractiveSchemaReportViewer,
            {
                componentProps: { report },
                opts: { maxWidth: 'lg', fullWidth: true },
            },
            undefined,
            true,
        )
    }

    return (
        <Box mt={1}>
            <Divider />
            <Box
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                px={1}
                py={0.5}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
                onClick={() => setExpanded(prev => !prev)}
            >
                <Box display='flex' alignItems='center' gap={1}>
                    <Assignment fontSize='small' color='primary' />
                    <Typography variant='subtitle2' fontWeight={600} fontSize={12}>
                        {t('activity-reports', { ns: 'interactive-schema' })}
                    </Typography>
                    <Chip label={reports.length} size='small' color='primary' sx={{ height: 18, fontSize: 11 }} />
                </Box>
                <IconButton size='small'>
                    {expanded ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
                </IconButton>
            </Box>

            <Collapse in={expanded}>
                <Table size='small'>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontSize: 11, py: 0.5 }}>{t('reportName', { ns: 'interactive-schema' })}</TableCell>
                            <TableCell sx={{ fontSize: 11, py: 0.5 }}>{t('activity', { ns: 'interactive-schema' })}</TableCell>
                            <TableCell sx={{ fontSize: 11, py: 0.5 }}>{t('date', { ns: 'common' })}</TableCell>
                            <TableCell sx={{ py: 0.5 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reports.map(report => (
                            <TableRow
                                key={report.id}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleRowClick(report)}
                            >
                                <TableCell sx={{ fontSize: 12, py: 0.5 }}>{report.reportName}</TableCell>
                                <TableCell sx={{ fontSize: 12, py: 0.5, color: 'text.secondary' }}>
                                    {report.schema?.name ?? '—'}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12, py: 0.5, color: 'text.secondary' }}>
                                    {new Date(report.startDate).toLocaleDateString('no-NO')}
                                </TableCell>
                                <TableCell align='right' sx={{ py: 0.5 }} onClick={e => e.stopPropagation()}>
                                    <Tooltip title={String(t('delete-report', { ns: 'interactive-schema' }))}>
                                        <IconButton size='small' onClick={e => handleDelete(e, report)}>
                                            <DeleteOutline fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Collapse>
        </Box>
    )
}